import {
  AiActionStatus,
  GetCallAiActionsQuery,
} from "@/graphql/generated/graphql";
import { UtteranceType } from "@/states/call/utterances/utterances.utility.decl";
import { toActionParamRows } from "./aiActionParams.helpers";
import { AIActionItem, TranscriptEntry } from "./aiActions.helpers.decl";

const MILLISECONDS_PER_SECOND = 1000;

type Timeline = GetCallAiActionsQuery["getCallTimelineByCallSidV2"];
type TimelineEvent = Extract<
  NonNullable<Timeline>,
  { __typename: "GetTypedCallTimelineOutputV2" }
>["events"][number];
type AIActionEvent = Extract<
  TimelineEvent,
  { __typename: "VoiceVirtualAgentAIActionEvent" }
>;

// "93.18948.getContact" -> "Get contact"
export function formatActionName(actionId: string) {
  return actionId
    .slice(actionId.lastIndexOf(".") + 1)
    .replace(/_/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .toLowerCase()
    .replace(/^./, (char) => char.toUpperCase());
}

type GetCallRelativeSecondsArgs = {
  occurredAt: string;
  durationMs?: number | null;
  anchor: string;
};

// occurredAt is the completion time, so the action started `durationMs` earlier.
export function getCallRelativeSeconds({
  occurredAt,
  durationMs,
  anchor,
}: GetCallRelativeSecondsArgs) {
  const startedAt = Date.parse(occurredAt) - (durationMs ?? 0);
  return Math.max(
    0,
    (startedAt - Date.parse(anchor)) / MILLISECONDS_PER_SECOND
  );
}

function isAIActionEvent(event: TimelineEvent): event is AIActionEvent {
  return event.__typename === "VoiceVirtualAgentAIActionEvent";
}

export function mapToAIActionItems(
  timeline: Timeline | undefined,
  anchor: string
): AIActionItem[] {
  if (timeline?.__typename !== "GetTypedCallTimelineOutputV2") {
    return [];
  }

  return timeline.events
    .filter(isAIActionEvent)
    .map((event) => ({
      id: `${event.actionId}-${event.occurredAt}`,
      actionName: formatActionName(event.actionId),
      applicationName: event.applicationName,
      integrationName: event.integrationName,
      iconUrl: event.iconUrl,
      inputParams: toActionParamRows(event.inputParams),
      outputParams: toActionParamRows(event.outputParams),
      isSuccess: event.status === AiActionStatus.SUCCESS,
      errorCategory: event.errorCategory,
      durationMs: event.duration,
      startTime: getCallRelativeSeconds({
        occurredAt: event.occurredAt,
        durationMs: event.duration,
        anchor,
      }),
    }))
    .sort((a, b) => a.startTime - b.startTime);
}

// Utterances keep their original index: transcript highlighting and sentiments are keyed by it.
export function mergeTranscriptEntries(
  utterances: UtteranceType[],
  actions: AIActionItem[]
): TranscriptEntry[] {
  const entries: TranscriptEntry[] = [];
  let actionIndex = 0;

  utterances.forEach((utterance, index) => {
    while (
      actionIndex < actions.length &&
      actions[actionIndex].startTime < utterance.startTime
    ) {
      entries.push({ kind: "aiAction", action: actions[actionIndex] });
      actionIndex += 1;
    }
    entries.push({ kind: "utterance", utterance, index });
  });

  for (const action of actions.slice(actionIndex)) {
    entries.push({ kind: "aiAction", action });
  }

  return entries;
}
